import { Box } from "@chakra-ui/react";
import { colors, customEvent, Spacing } from "goi_common";
import { BaseStack } from "goi_common/src/design-system";
import { ReactNode, useEffect, useState } from "react";
import { MubinsoConversionScreen } from "./MubinsoConversionScreen";
import { MubinsoLeadModal } from "./MubinsoLeadModal";
import { MubinsoTimeline } from "./MubinsoTimeline";
import {
  MubinsoAvailabilityList,
  MubinsoCompareTable,
  MubinsoCtaButton,
  MubinsoSavingBars,
  MubinsoScreenLayout,
  MubinsoStepEyebrow,
} from "./MubinsoUI";
import { buildMubinsoLeadMessage } from "./mubinsoLeadMessage";
import {
  FAMILY_TRACK_RECORD,
  mubinsoLeadStatus,
  MubinsoEntry,
  mubinsoEntryTrigger,
} from "./mubinsoConstants";

const TOTAL_STEPS = 4;

/**
 * 무빈소 절차 퍼널 B(처치군): 무빈소란 → 실제 진행 → 확인① 예상 비용 → 확인② 가능 장례식장 → 전환.
 *
 * A(MubinsoProcedureFunnelA)는 3화면 설명 후 "가이드 받아보기" 로 끝났다. 설명을 다 듣고도
 * 다음에 뭘 해야 하는지가 고객 몫으로 남아 리드 제출 단계에서 빠졌다(1달 28.6%, 여유 13.3%).
 * B는 종착점을 이해가 아니라 **준비 조건 확인 두 가지**(예상 비용, 가능 장례식장)로 옮기고,
 * 주 CTA 를 그 두 개를 한 번에 확인하는 것으로 바꿨다. 가이드는 보조 링크로 내렸다.
 *
 * 주제 "절차" 와 "세가지 다" 두 진입이 모두 이 화면으로 들어온다.
 * 실험 330 에서 이 두 진입만 B 가 채택돼 게이트 없이 확정 배치됐다(대조군 파일은 삭제).
 *
 * M5 가족장 퍼널(isFamilyFunnel)도 Q1 · Q2 를 거쳐 이 화면을 그대로 본다.
 * 화면은 같고 결과 제목 · 실적 문구와 결과 모달 문구만 가족장용이다(family prop).
 */
export default function MubinsoProcedureFunnelB({
  isUrgent,
  situationAnswer,
  topicAnswer,
  onComplete,
  entry,
  family = false,
}: {
  isUrgent: boolean;
  situationAnswer: string;
  topicAnswer: string;
  onComplete: () => void;
  /** 진입 구분. 퍼널별 개별 이벤트({p}-cta_main 등) 발화에 쓴다 */
  entry: MubinsoEntry;
  /** M5 가족장 경로. 결과 화면 · 모달 문구를 가족장용으로 바꾸고, 번호를 채우기 전에는 버튼을 끈다 */
  family?: boolean;
}) {
  const [step, setStep] = useState(1);
  const [openModal, setOpenModal] = useState<null | "check" | "guide">(null);

  // 단계 전환 시 스크롤 최상단 + 퍼널별 진행 이벤트.
  useEffect(() => {
    window.scrollTo(0, 0);
    customEvent({
      newGtm: { action: "mubinso_funnel_step", properties: { funnel: "procedure", step } },
    });
  }, [step]);

  const goNext = () => {
    customEvent({ newGtm: { path: "mubinso_funnel", trigger_and_target: `funnel-next-${step}` } });
    setStep((s) => s + 1);
  };

  // 1 - 무빈소란
  if (step === 1) {
    return (
      <MubinsoScreenLayout
        progress={{ current: 1, total: TOTAL_STEPS }}
        footer={<MubinsoCtaButton label="다음" onClick={goNext} />}
      >
        <Box width="100%" whiteSpace="pre-line" className="head2_b black">
          무빈소 장례는
          <br />
          <span style={{ color: colors.orange650 }}>빈소를 마련하지 않는</span> 장례예요
        </Box>
        <Spacing gap={14} />
        <Box width="100%" whiteSpace="pre-line" className="body_16_r gray600">
          {"조문을 받지 않고, 가족분들만\n고인을 보내드리는 형태예요."}
        </Box>

        <Spacing gap={24} />
        <MubinsoCompareTable
          rows={[
            { normal: "빈소를 차리고\n조문을 받아요", mubinso: "빈소 없이\n가족만 함께해요" },
            {
              normal: "이틀간 빈소를\n지키셔야 해요",
              mubinso: "자택에서 머물다\n입관식에 참여해요",
            },
            {
              normal: "조문객 음식, 제단꽃\n비용이 들어요",
              mubinso: "해당 항목이 없어\n비용이 줄어요",
            },
          ]}
        />

        <Spacing gap={16} />
        <BaseStack width="100%" bg={colors.orange200} borderRadius="16px" p="18px 20px">
          <Box className="body_14_m" color={colors.orange800}>
            수의, 입관, 발인, 화장 절차는 그대로 진행되고,{" "}
            <b style={{ color: colors.orange750 }}>조문을 받는 자리만 없을 뿐이에요.</b>
          </Box>
        </BaseStack>
      </MubinsoScreenLayout>
    );
  }

  // 2 - 실제 진행
  if (step === 2) {
    return (
      <MubinsoScreenLayout
        progress={{ current: 2, total: TOTAL_STEPS }}
        footer={<MubinsoCtaButton label="다음" onClick={goNext} />}
      >
        <Box width="100%" whiteSpace="pre-line" className="head2_b black">
          가족분들은 자택에 계시다가,
          <br />
          다음 날 <span style={{ color: colors.orange650 }}>입관식</span>에<br />
          참여하시면 됩니다
        </Box>
        <Spacing gap={14} />
        <Box width="100%" className="body_16_r gray600">
          형식은 간소해져도,{" "}
          <b style={{ color: colors.gray800, fontWeight: 600 }}>마음의 깊이는 다르지 않아요.</b>
        </Box>

        <Spacing gap={32} />
        <MubinsoTimeline
          items={[
            {
              phase: "장례 첫날",
              title: "자택에서 머무르세요",
              desc: "빈소를 지키지 않아도 괜찮아요.",
            },
            {
              phase: "다음 날 · 발인 전",
              title: "입관식에 참여해요",
              desc: "고인을 뵐 수 있는 마지막 자리예요.\n그만큼 가장 중요한 시간이에요.",
              highlight: true,
            },
            { phase: "이어서", title: "발인" },
          ]}
        />

        <Spacing gap={24} />
        <BaseStack width="100%" bg={colors.orange200} borderRadius="16px" p="18px 20px" spacing={8}>
          <Box className="body_14_m" color={colors.orange800}>
            무빈소장이라고 입관식을 생략하지는 않아요.
          </Box>
          <Box className="body_14_r gray600">
            입관식은 주관하는 <b style={{ color: colors.orange750 }}>장례지도사의 역량</b>에 따라
            달라지기 때문에, 어떤 장례지도사를 만나는지가 중요해요.
          </Box>
          <Box className="body_14_r gray600">
            장례식장 소속 지도사는 한 명이 여러 빈소를 함께 담당해, 한 가족에만 온전히 집중하기
            어려울 수 있어요.
          </Box>
        </BaseStack>
      </MubinsoScreenLayout>
    );
  }

  // 3 - 확인① 예상 비용
  if (step === 3) {
    return (
      <MubinsoScreenLayout
        progress={{ current: 3, total: TOTAL_STEPS }}
        footer={<MubinsoCtaButton label="다음" onClick={goNext} />}
      >
        <MubinsoStepEyebrow index={1} label="준비할 때 꼭 확인할 것" />
        <Spacing gap={10} />
        <Box width="100%" whiteSpace="pre-line" className="head2_b black">
          내 상황에 맞는
          <br />
          <span style={{ color: colors.orange650 }}>예상 비용</span>을 확인해야 해요
        </Box>
        <Spacing gap={14} />
        <Box width="100%" whiteSpace="pre-line" className="body_16_r gray600">
          {"무빈소는 기존 장례보다\n비용을 크게 줄일 수 있어요."}
        </Box>

        <Spacing gap={24} />
        {/* 금액을 넣지 않는다. 여기서 평균가를 알려주면 확인 없이 이탈한다(As-is 문제) */}
        <MubinsoSavingBars
          baseLabel="기존 가족장"
          baseNote="빈소, 음식, 제단 포함"
          savedLabel="무빈소 장례"
          savedNote="항목을 줄여 절감"
        />

        <Spacing gap={20} />
        <Box width="100%" className="body_16_r gray800">
          다만 장례식장 이용료, 이송 거리, 용품 선택에 따라 차이는 발생할 수 있기 때문에{" "}
          <b style={{ color: colors.gray900 }}>내 상황별 예상 비용은 확인해보시는 것이 좋아요.</b>
        </Box>

        <Spacing gap={20} />
        <BaseStack width="100%" bg={colors.orange200} borderRadius="16px" p="18px 20px">
          <Box className="body_14_m" color={colors.orange800}>
            내 상황에서 꼭 필요한 항목을 기준으로 확인하면,{" "}
            <b style={{ color: colors.orange750 }}>최대 700만 원까지도 줄일 수 있어요.</b>
          </Box>
        </BaseStack>
      </MubinsoScreenLayout>
    );
  }

  // 4 - 확인② 가능 장례식장
  if (step === 4) {
    return (
      <MubinsoScreenLayout
        progress={{ current: 4, total: TOTAL_STEPS }}
        footer={<MubinsoCtaButton label="다음" onClick={goNext} />}
      >
        <MubinsoStepEyebrow index={2} label="준비할 때 꼭 확인할 것" />
        <Spacing gap={10} />
        <Box width="100%" whiteSpace="pre-line" className="head2_b black">
          우리 지역,
          <br />
          <span style={{ color: colors.orange650 }}>무빈소 가능한 장례식장</span>을<br />
          찾아야 해요
        </Box>
        <Spacing gap={14} />
        <Box width="100%" whiteSpace="pre-line" className="body_16_r gray600">
          {"모든 장례식장에서 무빈소를\n진행할 수 있는 것은 아니에요."}
        </Box>

        <Spacing gap={24} />
        <MubinsoAvailabilityList
          items={[
            { text: "A 장례식장 · 무빈소 가능", available: true },
            { text: "B 장례식장 · 진행 불가", available: false },
            { text: "C 장례식장 · 진행 불가", available: false },
          ]}
        />

        <Spacing gap={20} />
        <Box width="100%" className="body_16_r gray800">
          장례식장 입장에서는 수익이 낮은 형태라 받지 않는 곳이 많아요. 고인이 계신 지역과 가까운 곳
          중에서{" "}
          <b style={{ color: colors.gray900 }}>무빈소 진행이 가능한 장례식장을 찾아야 합니다.</b>
        </Box>

        <Spacing gap={20} />
        <BaseStack width="100%" bg={colors.orange200} borderRadius="16px" p="18px 20px">
          <Box className="body_14_m" color={colors.orange800}>
            상황이 발생한 뒤 급하게 찾다가{" "}
            <b style={{ color: colors.orange750 }}>무빈소로 진행하지 못할 수도 있어요.</b> 미리
            확인하는 것이 좋아요.
          </Box>
        </BaseStack>
      </MubinsoScreenLayout>
    );
  }

  // 5 - 전환
  return (
    <>
      <MubinsoConversionScreen
        entry={entry}
        headline={
          family ? (
            // "한 번에" 는 360 폭에서 글 칸(312px)보다 1px 넘쳐 "요" 만 다음 줄로 갔다. "모두" 는 286px
            <KeepAll>
              장례식장과 예상 비용,
              <br />
              고이에서 <span style={{ color: colors.orange650 }}>모두</span> 확인해 드릴게요
            </KeepAll>
          ) : (
            <>
              장례식장과 예상 비용,
              <br />
              고이에서 <span style={{ color: colors.orange650 }}>한 번에</span> 확인해 드릴게요
            </>
          )
        }
        sub={
          family
            ? `${FAMILY_TRACK_RECORD}의 경험을 바탕으로\n내 상황에 맞는 내용만 정리해서 알려드려요.`
            : "무빈소 장례 1,000건 이상의 경험을 바탕으로\n내 상황에 맞는 내용만 정리해서 알려드려요."
        }
        checkItems={[
          { text: "내 지역에서 무빈소가 가능한 장례식장" },
          { text: "내 상황 기준의 예상 비용" },
          { text: "꼭 필요한 항목과 추가 비용 발생 가능 항목" },
        ]}
        reviewTitle="실제 무빈소 진행 가족분들의 후기"
        reviews={[
          {
            text: "“생소하거나 무성의할까 우려했는데, 그 어떤 화려한 장례보다도 정중하고 기품 있게 이끌어주셨어요.”",
            meta: "2026. 05. 11 작성 후기 · 이OO 고객님",
          },
          {
            text: "“가장 중요한 입관·발인 예식을 위해 새벽부터 꽃시장에서 생화를 넉넉히 준비해주셔서, 꽃집을 했던 누님이 많이 만족했어요.”",
            meta: "2026. 03. 13 작성 후기 · 구OO 고객님",
          },
        ]}
        primaryLabel="장례식장과 예상 비용 무료로 한 번에 확인하기"
        onPrimary={() => {
          // 위치 기준(주 CTA). 개편 전 baseline 과 이어지도록 이름을 유지한다
          customEvent({ newGtm: { path: "mubinso_funnel", trigger_and_target: "conv-info_cta" } });
          customEvent({
            newGtm: {
              path: "mubinso_funnel",
              trigger_and_target: mubinsoEntryTrigger(entry, "cta_main"),
            },
          });
          // 의미 기준(이 자리가 약속하는 것). 합산 지표에는 위치 이벤트만 쓸 것
          setOpenModal("check");
        }}
        secondaryLabel="무빈소 진행 가이드 받아보기"
        secondaryVariant="link"
        secondaryHint="아직 천천히 알아보는 중이라면"
        onSecondary={() => {
          // 상담이 아니라 자료 요청이라 conv-cnslt_cta 를 쓰지 않는다(상담 지표 오염 방지)
          // 가족장은 A 절차 진입 지표(prc)에 섞이지 않게 fam 으로 따로 센다
          customEvent({
            newGtm: {
              path: "mubinso_funnel",
              trigger_and_target: family ? "fam-guide_lnk" : "prc-guide_lnk",
            },
          });
          setOpenModal("guide");
        }}
      />

      <MubinsoLeadModal
        isOpen={openModal === "check"}
        onClose={() => setOpenModal(null)}
        {...(family
          ? {
              title: (
                <KeepAll>
                  내 상황에 맞는{" "}
                  <span style={{ whiteSpace: "nowrap" }}>
                    <span style={{ color: colors.orange650 }}>장례식장</span>과{" "}
                    <span style={{ color: colors.orange650 }}>예상 비용</span>을
                  </span>{" "}
                  보내드릴게요
                </KeepAll>
              ),
              description: (
                <KeepAll>
                  고이가 {FAMILY_TRACK_RECORD}의 경험을 바탕으로, 필요하신 정보와 자료를 빠르게
                  전해드릴게요.
                </KeepAll>
              ),
              buttonText: "동의하고 자료 받아보기",
            }
          : {
              title: "무빈소 가능 장례식장과\n예상 비용을 확인해 드릴게요",
              description:
                "연락처를 남겨주시면 내 지역에서 무빈소\n진행이 가능한 장례식장과 상황에 맞는\n예상 비용을 함께 확인해 안내드려요.",
              // 모달 버튼은 "동의하고 OO하기" 로 통일한다. 바로 위에 개인정보 수집 동의가 붙어 있어
              // 버튼이 동의 행위를 드러내야 한다(디자인은 "무료로 한 번에 확인하기")
              buttonText: "동의하고 한 번에 확인하기",
            })}
        disableUntilFilled={family}
        wide={family}
        messageBuilder={() =>
          buildMubinsoLeadMessage({
            ctaType: "check",
            situation: situationAnswer,
            topic: topicAnswer,
          })
        }
        leadStatus={mubinsoLeadStatus(situationAnswer)}
        entry={entry}
        onSubmitted={onComplete}
      />
      <MubinsoLeadModal
        isOpen={openModal === "guide"}
        onClose={() => setOpenModal(null)}
        {...(family
          ? {
              title: (
                <KeepAll>
                  <span style={{ color: colors.orange650 }}>가족장 가이드</span> 받아보기
                </KeepAll>
              ),
              description: (
                <KeepAll>
                  고이가 {FAMILY_TRACK_RECORD}의 경험을 바탕으로, 절차와 예상 비용, 준비할 것을
                  정리해 전해드릴게요.
                </KeepAll>
              ),
            }
          : {
              title: "무빈소 가이드 받아보기",
              freeTag: true,
              description:
                "무빈소 장례 1,000건 이상을 진행하며\n가족분들이 가장 궁금해하셨던 내용을 담았어요.\n절차, 예상 비용, 장례 준비까지 알려드려요.",
            })}
        disableUntilFilled={family}
        wide={family}
        urgentNote={isUrgent}
        buttonText="동의하고 자료 받아보기"
        messageBuilder={() =>
          buildMubinsoLeadMessage({
            ctaType: "info",
            situation: situationAnswer,
            topic: topicAnswer,
          })
        }
        leadStatus={mubinsoLeadStatus(situationAnswer)}
        entry={entry}
        // 결과 화면 신청과 나눠 보려고 가이드 창 제출만 따로 센다(가족장은 E4 비교 밖이라 쏘지 않는다)
        onLeadSuccess={() => {
          if (family) return;
          customEvent({ newGtm: { path: "mubinso_funnel", trigger_and_target: "prc-guide_ok" } });
        }}
        onSubmitted={onComplete}
      />
    </>
  );
}

/** 가족장 모달 문구는 강제 줄바꿈 없이 단어 단위로만 줄을 바꾼다("보내/드릴게요" 방지) */
function KeepAll({ children }: { children: ReactNode }) {
  return <span style={{ wordBreak: "keep-all" }}>{children}</span>;
}
